import React from 'react';
import { Shield, AlertTriangle } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="py-20 animate-fade-in">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex p-4 glass-card rounded-2xl mb-6">
              <Shield className="w-12 h-12 text-primary" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
            <p className="text-muted-foreground">
              Last Updated: January 1, 2024
            </p>
          </div>

          {/* Content */}
          <div className="glass-card p-8 lg:p-12 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                At NoTrackTools (“we,” “us,” or “our”), we are committed to protecting your privacy.
                This Privacy Policy explains how we handle information when you use our website
                and tools (collectively, the “Service”).
              </p>
            </section>

            {/* Core Privacy Principle */}
            <section className="glass p-6 rounded-lg border border-primary/20">
              <h2 className="text-2xl font-bold mb-4 text-primary">Our Core Privacy Principle</h2>
              <p className="text-foreground font-semibold leading-relaxed">
                We do not collect, store, or process any personal data or user-generated content.
                All operations on our tools run entirely in your browser using client-side
                JavaScript. Your data never leaves your device.
              </p>
            </section>

            {/* Information We Don't Collect */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Information We Do NOT Collect</h2>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start">
                  <span className="text-destructive mr-2">✗</span>
                  Personal information (name, email, phone, address)
                </li>
                <li className="flex items-start">
                  <span className="text-destructive mr-2">✗</span>
                  Files or data you process using our tools
                </li>
                <li className="flex items-start">
                  <span className="text-destructive mr-2">✗</span>
                  Passwords or encryption keys
                </li>
                <li className="flex items-start">
                  <span className="text-destructive mr-2">✗</span>
                  Usage patterns or behavioral data
                </li>
                <li className="flex items-start">
                  <span className="text-destructive mr-2">✗</span>
                  IP addresses or location data
                </li>
                <li className="flex items-start">
                  <span className="text-destructive mr-2">✗</span>
                  Browser fingerprints or device information
                </li>
              </ul>
            </section>

            {/* Third-Party Advertising */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Third-Party Advertising (Google AdSense)</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use Google AdSense to display advertisements on our website. Google AdSense
                may use cookies and web beacons to serve ads based on your prior visits to our
                website or other websites.
              </p>
              <div className="glass p-4 rounded-lg mb-4">
                <p className="text-sm font-semibold text-foreground mb-2">
                  Important: Displaying ads does NOT mean we collect your data.
                </p>
                <p className="text-sm text-muted-foreground">
                  We earn revenue through ads, but we never access, store, or share your personal
                  information. Ad networks operate independently of our tool functionality.
                </p>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You may opt out of personalized advertising by visiting:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>
                  <a
                    href="https://www.google.com/settings/ads"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Google Ads Settings
                  </a>
                </li>
                <li>
                  <a
                    href="http://www.aboutads.info/choices/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Digital Advertising Alliance Opt-Out
                  </a>
                </li>
              </ul>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Cookies</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We only use essential cookies to remember your theme preference (light/dark mode).
                Third-party advertising services may set their own cookies as described above.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Data Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                Since we do not collect or store any user data, there is no data to secure on our
                servers. All processing happens locally in your browser using standard Web APIs
                (Web Crypto API, Canvas API, File API).
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Your Rights (GDPR & CCPA)</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Since we do not collect personal data, there is no data to access, modify, or
                delete. However, you have the right to:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start">
                  <span className="text-success mr-2">✓</span>
                  Opt out of personalized advertising (see links above)
                </li>
                <li className="flex items-start">
                  <span className="text-success mr-2">✓</span>
                  Clear your browser cookies at any time
                </li>
                <li className="flex items-start">
                  <span className="text-success mr-2">✓</span>
                  Use ad blockers or privacy-focused browsers
                </li>
              </ul>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our Service does not knowingly collect information from children under 13. Since
                we collect no data at all, our Service is inherently safe for all ages.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any
                changes by posting the new Privacy Policy on this page with an updated “Last
                Updated” date.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at:
                <a
                  href="mailto:praneethartha381@gmail.com"
                  className="block mt-2 text-primary hover:underline"
                >
                  praneethartha381@gmail.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
